import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../../components/Header';
import api from '../../services/api';
import {
  showSuccess,
  showError
} from '../../services/alert';

const RomaneioSafraForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const isEditing = !!id;

  const [safras, setSafras] = useState([]);
  const [colheitas, setColheitas] = useState([]);
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    safraId: '',
    colheitaId: '',
    dataEntrega: new Date().toISOString().substring(0, 10),
    motorista: '',
    caminhao: '',
    placa: '',
    pesoBruto: '',
    pesoTara: '',
    umidade: '',
    impureza: '',
    condicaoProduto: '',
    destino: '',
    observacao: ''
  });

  useEffect(() => {
    carregarDados();
  }, []);

  useEffect(() => {
    if (isEditing) {
      carregarRegistro();
    }
  }, [id, isEditing]);

  const carregarDados = async () => {
    try {
      const [safrasRes, colheitasRes] = await Promise.all([
        api.get('/safras'),
        api.get('/colheita')
      ]);

      setSafras(safrasRes.data || []);
      setColheitas(colheitasRes.data || []);
    } catch {
      showError('Erro ao carregar dados.');
    }
  };

  const carregarRegistro = async () => {
    try {
      setLoading(true);

      const response = await api.get(`/RomaneioSafra/${id}`);
      const d = response.data;

      setForm({
        safraId: d.safraId?.toString() || '',
        colheitaId: d.colheitaId?.toString() || '',
        dataEntrega: d.dataEntrega?.substring(0, 10) || '',
        motorista: d.motorista || '',
        caminhao: d.caminhao || '',
        placa: d.placa || '',
        pesoBruto: d.pesoBruto?.toString() || '',
        pesoTara: d.pesoTara?.toString() || '',
        umidade: d.umidade?.toString() || '',
        impureza: d.impureza?.toString() || '',
        condicaoProduto: d.condicaoProduto || '',
        destino: d.destino || '',
        observacao: d.observacao || ''
      });
    } catch {
      showError('Erro ao carregar romaneio.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const pesoLiquido = useMemo(() => {
    const bruto = parseFloat(form.pesoBruto) || 0;
    const tara = parseFloat(form.pesoTara) || 0;

    return bruto - tara;
  }, [form.pesoBruto, form.pesoTara]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        safraId: Number(form.safraId),
        colheitaId: Number(form.colheitaId) || null,
        dataEntrega: new Date(form.dataEntrega).toISOString(),
        motorista: form.motorista.trim(),
        caminhao: form.caminhao.trim(),
        placa: form.placa.trim().toUpperCase(),
        pesoBruto: parseFloat(form.pesoBruto),
        pesoTara: parseFloat(form.pesoTara),
        umidade: form.umidade ? parseFloat(form.umidade) : null,
        impureza: form.impureza ? parseFloat(form.impureza) : null,
        condicaoProduto: form.condicaoProduto.trim(),
        destino: form.destino.trim(),
        observacao: form.observacao
      };

      if (isEditing) {
        await api.put(`/RomaneioSafra/${id}`, payload);
        showSuccess('Romaneio atualizado.');
      } else {
        await api.post('/RomaneioSafra', payload);
        showSuccess('Romaneio cadastrado.');
      }

      navigate('/romaneio-safra');
    } catch (error) {
      showError(
        error?.response?.data?.message ||
        'Erro ao salvar.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mt-4">
      <Header />

      <div className="card shadow-lg border-0 rounded-3">
        <div className="card-header bg-success text-white d-flex align-items-center gap-3 rounded-top-3">
          <button
            type="button"
            className="btn btn-light btn-sm d-flex align-items-center gap-1"
            onClick={() => navigate('/romaneio-safra')}
            disabled={loading}
          >
            <i className="bi bi-arrow-left text-success" />
            <span className="text-success fw-semibold">Voltar</span>
          </button>

          <h5 className="mb-0">
            {isEditing ? 'Editar Romaneio' : 'Novo Romaneio'}
          </h5>
        </div>

        <div className="card-body">
          <form onSubmit={handleSubmit} className="row g-3">
            <div className="col-md-4">
              <label className="form-label fw-semibold">Safra *</label>
              <select
                name="safraId"
                value={form.safraId}
                onChange={handleChange}
                className="form-select"
                required
                disabled={loading}
              >
                <option value="">Selecione...</option>
                {safras.map(s => (
                  <option key={s.id} value={s.id}>
                    {s.cultura}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-4">
              <label className="form-label fw-semibold">Colheita</label>
              <select
                name="colheitaId"
                value={form.colheitaId}
                onChange={handleChange}
                className="form-select"
                disabled={loading}
              >
                <option value="">Nenhuma</option>
                {colheitas.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.quantidade} {c.unidade}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-4">
              <label className="form-label fw-semibold">Data Entrega *</label>
              <input
                type="date"
                name="dataEntrega"
                value={form.dataEntrega}
                onChange={handleChange}
                className="form-control"
                required
                disabled={loading}
              />
            </div>

            <div className="col-md-4">
              <label className="form-label fw-semibold">Motorista</label>
              <input
                name="motorista"
                value={form.motorista}
                onChange={handleChange}
                className="form-control"
                disabled={loading}
              />
            </div>

            <div className="col-md-4">
              <label className="form-label fw-semibold">Caminhão</label>
              <input
                name="caminhao"
                value={form.caminhao}
                onChange={handleChange}
                className="form-control"
                disabled={loading}
              />
            </div>

            <div className="col-md-4">
              <label className="form-label fw-semibold">Placa</label>
              <input
                name="placa"
                value={form.placa}
                onChange={handleChange}
                className="form-control"
                disabled={loading}
              />
            </div>

            <div className="col-md-4">
              <label className="form-label fw-semibold">Peso Bruto *</label>
              <input
                type="number"
                step="0.01"
                name="pesoBruto"
                value={form.pesoBruto}
                onChange={handleChange}
                className="form-control"
                required
                disabled={loading}
              />
            </div>

            <div className="col-md-4">
              <label className="form-label fw-semibold">Peso Tara *</label>
              <input
                type="number"
                step="0.01"
                name="pesoTara"
                value={form.pesoTara}
                onChange={handleChange}
                className="form-control"
                required
                disabled={loading}
              />
            </div>

            <div className="col-md-4">
              <label className="form-label fw-semibold">Peso Líquido</label>
              <input
                value={pesoLiquido}
                className="form-control bg-light"
                disabled
              />
            </div>

            <div className="col-md-3">
              <label className="form-label fw-semibold">Umidade (%)</label>
              <input
                type="number"
                step="0.01"
                name="umidade"
                value={form.umidade}
                onChange={handleChange}
                className="form-control"
                disabled={loading}
              />
            </div>

            <div className="col-md-3">
              <label className="form-label fw-semibold">Impureza (%)</label>
              <input
                type="number"
                step="0.01"
                name="impureza"
                value={form.impureza}
                onChange={handleChange}
                className="form-control"
                disabled={loading}
              />
            </div>

            <div className="col-md-6">
              <label className="form-label fw-semibold">Condição do produto</label>
              <input
                name="condicaoProduto"
                value={form.condicaoProduto}
                onChange={handleChange}
                className="form-control"
                disabled={loading}
              />
            </div>

            <div className="col-md-6">
              <label className="form-label fw-semibold">Destino</label>
              <input
                name="destino"
                value={form.destino}
                onChange={handleChange}
                className="form-control"
                disabled={loading}
              />
            </div>

            <div className="col-12">
              <label className="form-label fw-semibold">Observação</label>
              <textarea
                rows="4"
                name="observacao"
                value={form.observacao}
                onChange={handleChange}
                className="form-control"
                disabled={loading}
              />
            </div>

            <div className="col-12 d-flex justify-content-end mt-3">
              <button
                type="submit"
                className="btn btn-success me-2 d-flex align-items-center gap-1"
                disabled={loading}
              >
                <i className="bi bi-save"></i>
                <span>{loading ? 'Salvando...' : 'Salvar'}</span>
              </button>

              <button
                type="button"
                className="btn btn-secondary d-flex align-items-center gap-1"
                onClick={() => navigate('/romaneio-safra')}
                disabled={loading}
              >
                <i className="bi bi-x-circle"></i>
                <span>Cancelar</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RomaneioSafraForm;